/**
 * HeaderMenu custom element — wraps the whole desktop header (§8cx).
 * Manages hover/focus for every menu list inside it: the Stacked menu row and,
 * in the Inline layout, the menu on each side of the logo. Listeners are
 * delegated from this element, so items keep working when the Inline → Stacked
 * fallback (inline script in sections/global--header.liquid) moves them from
 * one list to another. One submenu is open at a time across all lists.
 *
 * How the animation works:
 * - .menu-list__submenu starts with clip-path: inset(0 0 100% 0) — nothing visible
 * - On activate, aria-expanded="true" is set on the link
 * - CSS :has([aria-expanded='true']) selector transitions clip-path to inset(0) — full panel revealed
 * - No JS height measurement needed; the CSS animation is fully self-contained
 *
 * Escape closes the open panel, even one held open by hover alone: the item
 * gets data-dismissed, which the CSS reveal rules skip, until the pointer or
 * focus leaves it (WCAG 1.4.13: dismissable without moving the pointer).
 */
let activeItem = null;

function closeItem(item) {
  item.querySelector('.menu-list__link')?.setAttribute('aria-expanded', 'false');
  if (item === activeItem) activeItem = null;
}

class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.addEventListener('pointerover', this.#activate);
    this.addEventListener('pointerout', this.#deactivate);
    this.addEventListener('focusin', this.#activate);
    this.addEventListener('focusout', this.#deactivate);

    window.addEventListener('resize', this.#onResize);
    // On the document: a panel opened by hover has no focus inside it
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onResize = () => {
    if (activeItem) closeItem(activeItem);
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    // activeItem, or a panel CSS :hover alone holds open (e.g. after a resize)
    const item = activeItem || this.querySelector('.menu-list__list-item:hover');
    if (!item?.querySelector('.menu-list__submenu')) return;
    item.setAttribute('data-dismissed', '');
    closeItem(item);
    // Focus was inside the panel: hand it back to the item's link
    if (item.contains(document.activeElement)) item.querySelector('.menu-list__link')?.focus();
  };

  #activate = (event) => {
    const item = event.target.closest('.menu-list__list-item');
    if (!item || item === activeItem || item.hasAttribute('data-dismissed') || !item.querySelector('.menu-list__submenu')) return;

    if (activeItem) closeItem(activeItem);

    activeItem = item;
    // Sticky header: the panel's max-height is the viewport below the header's
    // bottom edge (theme.css), which depends on scroll position — take it now
    const header = this.closest('.site-header');
    header?.style.setProperty('--header-bottom', `${Math.round(header.getBoundingClientRect().bottom)}px`);
    item.querySelector('.menu-list__link')?.setAttribute('aria-expanded', 'true');
  };

  #deactivate = (event) => {
    const item = event.target.closest('.menu-list__list-item');
    if (!item) return;

    // Stay open while pointer/focus moves within the item (link ↔ its submenu)
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

    item.removeAttribute('data-dismissed');
    closeItem(item);
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// Mobile sub-panel navigation. Delegated from the document (§8du): the header script
// builds the mobile menu from the blocks' templates, and the theme editor can replace the
// whole header, so listeners on the elements themselves would go stale.
const mobileMenu = () => document.getElementById('MobileMenu');
const mainPanel = () => document.getElementById('MobileMenuMain');

// The panel off to the side is inert, so Tab stays in the one on screen; focus follows
// the move (Back on the way in, the item's button on the way out).
function openSubPanel(panelId) {
  const sub = document.getElementById(panelId);
  if (!sub) return;
  const main = mainPanel();
  main?.classList.add('is-pushed');
  if (main) main.inert = true;
  sub.classList.add('is-active');
  sub.setAttribute('aria-hidden', 'false');
  sub.inert = false;
  sub.querySelector('[data-back]')?.focus({ preventScroll: true });
}

function closeSubPanel(sub) {
  sub.classList.remove('is-active');
  sub.setAttribute('aria-hidden', 'true');
  sub.inert = true;
}

function showMainPanel() {
  const main = mainPanel();
  main?.classList.remove('is-pushed');
  if (main) main.inert = false;
}

function closeAllSubPanels() {
  // Only touch panels that are actually open — avoids rAF races with is-pushed
  mobileMenu()?.querySelectorAll('.mobile-menu__panel--sub.is-active').forEach(closeSubPanel);
  showMainPanel();
  mobileMenu()?.querySelectorAll('.mobile-menu__link--parent[aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
}

if (!window.lickMobileMenuBound) {
  window.lickMobileMenuBound = true;
  document.addEventListener('click', (event) => {
    const menu = mobileMenu();
    if (!menu) return;
    // Open a sub-panel when a parent item is tapped
    const parent = event.target.closest('.mobile-menu__link--parent');
    if (parent && menu.contains(parent)) {
      closeAllSubPanels();
      openSubPanel(parent.dataset.panel);
      parent.setAttribute('aria-expanded', 'true');
      return;
    }
    // Back buttons
    const back = event.target.closest('[data-back]');
    if (back && menu.contains(back)) {
      const sub = back.closest('.mobile-menu__panel--sub');
      if (sub) closeSubPanel(sub);
      showMainPanel();
      const trigger = menu.querySelector(`[data-panel="${sub?.id}"]`);
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus({ preventScroll: true });
      return;
    }
    // Reset sub-panels when the mobile menu closes
    if (event.target.closest('#MobileMenuClose, #MobileMenuOverlay')) closeAllSubPanels();
  });
  // Escape closes the whole menu (theme.js): reset its sub-panels with it
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllSubPanels();
  });
}
